/**
 * DeleteAccountPage — Public RGPD-compliant account deletion request page.
 * User enters email → confirmation modal → backend sends verification email.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Mail, ArrowLeft, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import config from 'config';

const API = `${config.API_URL}/api`;

export default function DeleteAccountPage() {
  const [email, setEmail] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError('');
    setShowConfirm(true);
  };

  const handleConfirmDeletion = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/public/request-account-deletion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult('success');
        setShowConfirm(false);
      } else {
        setError(data.detail || 'Une erreur est survenue.');
        setShowConfirm(false);
      }
    } catch {
      setError('Impossible de contacter le serveur.');
      setShowConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  if (result === 'success') {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-4">
        <div className="bg-[#1F1F1F] border border-[#333] rounded-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#C9A24D] mb-3" data-testid="deletion-email-sent-title">Email de confirmation envoyé</h1>
          <p className="text-[#E5E5E5]/70 text-sm leading-relaxed mb-6">
            Un email de confirmation a été envoyé à <strong className="text-[#E5E5E5]">{email}</strong>.
            Cliquez sur le lien dans l'email pour confirmer votre demande de suppression.
          </p>
          <p className="text-[#E5E5E5]/40 text-xs mb-6">Le lien est valable 48 heures.</p>
          <Link to="/">
            <Button variant="outline" className="border-[#C9A24D]/30 text-[#E5E5E5]">
              <ArrowLeft className="w-4 h-4 mr-2" />Retour à l'accueil
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-4">
      <div className="bg-[#1F1F1F] border border-[#333] rounded-xl p-8 max-w-md w-full">
        <Link to="/" className="inline-flex items-center text-[#E5E5E5]/50 text-sm hover:text-[#E5E5E5] mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" />Retour
        </Link>

        <div className="text-center mb-6">
          <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-[#C9A24D]" data-testid="delete-account-title">Supprimer mon compte</h1>
          <p className="text-[#E5E5E5]/50 text-sm mt-2">
            Conformément au RGPD (Art. 17 — Droit à l'effacement), vous pouvez demander la suppression de votre compte et de vos données personnelles.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-[#E5E5E5]/70 mb-1.5 block">Adresse email de votre compte</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                className="pl-10 bg-[#1A1A1A] border-[#333] text-white"
                data-testid="delete-account-email"
              />
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="delete-account-error">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-red-500 hover:bg-red-600 text-white font-medium"
            data-testid="delete-account-submit"
          >
            Demander la suppression
          </Button>
        </form>

        <div className="mt-6 pt-4 border-t border-white/5">
          <h3 className="text-xs font-medium text-[#E5E5E5]/50 uppercase tracking-wider mb-2">Ce qui sera supprimé :</h3>
          <ul className="space-y-1.5 text-sm text-[#E5E5E5]/40">
            <li className="flex items-center gap-2"><span className="w-1 h-1 bg-red-400 rounded-full" />Votre profil et informations personnelles</li>
            <li className="flex items-center gap-2"><span className="w-1 h-1 bg-red-400 rounded-full" />Votre historique de réservations (anonymisé)</li>
            <li className="flex items-center gap-2"><span className="w-1 h-1 bg-red-400 rounded-full" />Vos messages et données de contact</li>
          </ul>
          <p className="text-xs text-[#E5E5E5]/30 mt-3">
            Les données de facturation sont conservées 5 ans conformément aux obligations comptables (Art. L123-22 du Code de Commerce).
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-[#1F1F1F] border border-red-500/30 rounded-xl p-6 max-w-sm w-full">
            <div className="text-center mb-4">
              <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-2" />
              <h2 className="text-lg font-bold text-[#E5E5E5]">Confirmer la suppression</h2>
            </div>
            <p className="text-[#E5E5E5]/60 text-sm text-center mb-1">
              Vous allez demander la suppression de votre compte associé à :
            </p>
            <p className="text-[#C9A24D] font-medium text-center mb-4">{email}</p>
            <p className="text-red-400/80 text-xs text-center mb-6">
              Un email de confirmation sera envoyé. La suppression ne sera effective qu'après validation par notre équipe.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-white/10 text-[#E5E5E5]"
                onClick={() => setShowConfirm(false)}
                disabled={loading}
                data-testid="delete-confirm-cancel"
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={handleConfirmDeletion}
                disabled={loading}
                data-testid="delete-confirm-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
